import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { UpdateAgentFormType, updateAgentSchema } from "../schema/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import AgentAgencyForm from "./AgentAgencyForm";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import SearchComponent from "./SearchComponent";
import axiosInstance from "@/services/api/axiosInstance";
import { toast } from "sonner";
import StickyFooter from "@/components/StickyFooter/StickyFooter";
import Button from "@mui/joy/Button";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LinkButton from "@/components/LinkButton/LinkButton";
import { ADMIN_ROUTE_PREFIX } from "@/types/constants/common";
import { AgentData } from "../constants/types";

const UpdateAgentForm = () => {
  const [agent, setAgent] = useState<AgentData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  useEffect(() => {
    if (agent !== null) {
      const { FirstName, LastName, EmailAddress, Phone, Address, SourceAgentId, SourceAgencyId, IsActive, Role } =
        agent;
      setValue("firstname", FirstName ?? "");
      setValue("lastname", LastName ?? "");
      setValue("ttandEmail", EmailAddress ?? "");
      setValue("businessPhone", Phone?.replace(/-/g, "") ?? "");
      setValue("city", Address?.City ?? "");
      setValue("province", Address?.State ?? "");
      setValue("postalCode", Address?.PostalCode ?? "");
      setValue("streetAddress", Address?.Address1 ?? "");
      setValue("brandedAs", Address?.BusinessName);
      setValue("agentId", SourceAgentId);
      setValue("clientlinqBranch", SourceAgencyId);
      setValue("status", IsActive);
      setValue("agentType", Role ?? "");
    }
  }, [agent]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<UpdateAgentFormType>({
    resolver: zodResolver(updateAgentSchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      streetAddress: "",
      city: "",
      province: "",
      postalCode: "",
      ttandEmail: "",
      businessPhone: "",
      brandedAs: "",
      agentId: "",
      clientlinqBranch: "",
      status: false,
    },
  });

  const onSubmit = async (data: UpdateAgentFormType) => {
    setIsSubmitting(true);
    try {
      // format updateAgent -> Constria
      const formatedData = {
        SourceAgencyId: data.clientlinqBranch,
        FirstName: data.firstname,
        LastName: data.lastname,
        EmailAddress: data.ttandEmail,
        Phone: data.businessPhone,
        Role: data.agentType,
        SourceAgentId: data.agentId,
        IsActive: data.status,
        Address: {
          BusinessName: data.brandedAs,
          Address1: data.streetAddress,
          Address2: "",
          City: data.city,
          State: data.province,
          PostalCode: data.postalCode,
          Zip: data.postalCode,
          Country: "CA",
        },
      };
      const result = await axiosInstance.put(`/admin/constria/update-agent`, formatedData);
      toast.success(result.data?.message || "Agent Updated Successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something Went Wrong");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Stack>
      <Typography
        level="h3"
        sx={{
          mb: 4,
        }}
      >
        {"Update Agent"}
      </Typography>
      <SearchComponent type={"agent"} onDataFetched={setAgent} mode={"update"} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={"row"} gap={4}>
          <AgentAgencyForm
            mode={"update"}
            type={"agent"}
            register={register}
            control={control}
            setValue={setValue}
            setError={setError}
            errors={errors}
            disable={!agent}
          />
        </Stack>
        <StickyFooter sx={{ mt: 4 }}>
          <Button
            type="submit"
            sx={{ textTransform: "capitalize" }}
            loading={isSubmitting}
            disabled={isSubmitting || !agent}
            startDecorator={<AddOutlinedIcon />}
          >
            Update Agent
          </Button>
          {agent && agent.sso && (
            <LinkButton
              color="primary"
              variant="outlined"
              target="_blank"
              rel="noopener noreferrer"
              to={agent.sso}
              disabled={isSubmitting}
            >
              View SSO
            </LinkButton>
          )}
          <LinkButton color="neutral" variant="outlined" to={`${ADMIN_ROUTE_PREFIX}/`} disabled={isSubmitting}>
            Cancel
          </LinkButton>
        </StickyFooter>
      </form>
    </Stack>
  );
};

export default UpdateAgentForm;
